import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addNotification } from '@redux/features/notificationsSlice';
import { getCurrentSession } from '@redux/features/sessionsSlice';
import { getCurrentUser, logoutUser } from '@redux/features/authSlice';
import { Loader } from 'components/Loader';
import styles from './ProtectedLayout.scss';

export const AuthGuard = ({ onReady, children }) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const isSessionLoading = useSelector((state) => state.sessions.isLoading);
  const sessionError = useSelector((state) => state.sessions.error);

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  const isAuthLoading = useSelector((state) => state.auth.isLoading);

  const locations = useSelector((state) => state.locations.locations);

  const { locationName } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initializationRef = useRef({
    sessionRequested: false,
    userRequested: false,
    sessionTimeout: null,
    readyDispatched: false,
  });

  const [loadingState, setLoadingState] = useState({
    waitingUser: false,
    waitingSession: false,
  });

  useEffect(() => {
    return () => {
      if (initializationRef.current.sessionTimeout) {
        clearTimeout(initializationRef.current.sessionTimeout);
      }
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && !user && !isAuthLoading && !initializationRef.current.userRequested) {
      initializationRef.current.userRequested = true;
      setLoadingState((prev) => ({ ...prev, waitingUser: true }));
      dispatch(getCurrentUser());
    }
  }, [isAuthenticated, user, isAuthLoading, dispatch]);

  useEffect(() => {
    if (
      isAuthenticated &&
      user &&
      !currentSession &&
      !isSessionLoading &&
      !initializationRef.current.sessionRequested
    ) {
      initializationRef.current.sessionRequested = true;
      setLoadingState((prev) => ({ ...prev, waitingSession: true }));

      dispatch(getCurrentSession());

      initializationRef.current.sessionTimeout = setTimeout(() => {
        if (!currentSession) {
          setLoadingState((prev) => ({ ...prev, waitingSession: false }));
          dispatch(logoutUser());
        }
      }, 15000);
    }
  }, [isAuthenticated, user, currentSession, isSessionLoading, dispatch]);

  useEffect(() => {
    if (user && initializationRef.current.userRequested) {
      setLoadingState((prev) => ({ ...prev, waitingUser: false }));
    }
  }, [user]);

  useEffect(() => {
    if (currentSession && initializationRef.current.sessionTimeout) {
      clearTimeout(initializationRef.current.sessionTimeout);
      initializationRef.current.sessionTimeout = null;
      setLoadingState((prev) => ({ ...prev, waitingSession: false }));
    }
  }, [currentSession]);

  useEffect(() => {
    if (!isAuthenticated) {
      initializationRef.current.sessionRequested = false;
      initializationRef.current.userRequested = false;
      setLoadingState({ waitingUser: false, waitingSession: false });

      if (initializationRef.current.sessionTimeout) {
        clearTimeout(initializationRef.current.sessionTimeout);
        initializationRef.current.sessionTimeout = null;
      }
    }
  }, [isAuthenticated]);

  const availableLocations = useMemo(() => {
    if (!user || !locations.length) return [];

    return locations.filter(({ id }) => {
      if (user.role === 3) return true;

      const userLocationIDs = user.locations?.map(({ id }) => id) || [];
      return userLocationIDs.includes(id);
    });
  }, [user, locations]);

  useEffect(() => {
    if (!locationName || !user || !locations.length) return;

    const availableTransliterations = availableLocations.map(({ transliteration }) => transliteration);

    if (!availableTransliterations.includes(locationName)) {
      dispatch(
        addNotification({
          id: Date.now(),
          title: 'У вас немає доступу до цієї зали',
          type: 'error',
        })
      );
      navigate('/locations/choose', { replace: true });
    }
  }, [user, locations, locationName, availableLocations, dispatch, navigate]);

  const isInitialLoading = loadingState.waitingUser || loadingState.waitingSession;

  useEffect(() => {
    if (!initializationRef.current.readyDispatched && !isInitialLoading && isAuthenticated && !sessionError) {
      initializationRef.current.readyDispatched = true;
      onReady?.();
    }
  }, [isInitialLoading, isAuthenticated, sessionError, onReady]);

  if (!isAuthenticated) {
    return <Navigate to='/auth' replace />;
  }

  if (isInitialLoading) {
    return (
      <div className={styles.globalLoading}>
        <Loader size='5' isActive />
        <div className={styles.title}>Завантаження</div>
        {loadingState.waitingUser && <div className={styles.details}>Отримання даних користувача...</div>}
        {loadingState.waitingSession && <div className={styles.details}>Перевірка активної сесії...</div>}
      </div>
    );
  }

  if (sessionError && initializationRef.current.sessionRequested) {
    console.log('Session error, logging out:', sessionError);
    dispatch(logoutUser());
    return <Navigate to='/auth' replace />;
  }

  return children || null;
};
